/**
 * PaymentMethodsPage - Payment Method Management
 * 
 * Displays and manages payment methods.
 * 
 * @validates Requirements 1-8 - Payment methods management
 */

import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaymentMethodStore } from '../store/paymentMethodStore';
import { usePaymentMethodTemplatesStore } from '../store/paymentMethodTemplatesStore';
import { useAuthStore } from '../store/authStore';
import { PaymentMethod, CreatePaymentMethodRequest } from '@budgira/shared';
import EmojiPicker from '../components/EmojiPicker';

// Icons
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CreditCardIcon = () => (
  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const RemoveIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
);

const PurgeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

interface PaymentMethodNodeProps {
  paymentMethod: PaymentMethod;
  onEdit: (pm: PaymentMethod) => void;
  onDelete: (pm: PaymentMethod) => void;
  canEdit: boolean;
}

const PaymentMethodNode = ({ paymentMethod, onEdit, onDelete, canEdit }: PaymentMethodNodeProps) => {
  const { t } = useTranslation();
  
  return (
    <div className="group flex items-center py-3 px-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors">
      {/* Logo/Icon */}
      <span className="mr-3 text-xl flex-shrink-0">
        {paymentMethod.logoUrl || <CreditCardIcon />}
      </span>

      {/* Name */}
      <span className="flex-1 font-medium text-slate-700 dark:text-slate-200">
        {paymentMethod.name}
      </span>

      {/* Uses Checkbook badge */}
      {paymentMethod.usesCheckbook && (
        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/50 text-violet-600 dark:text-violet-400 mr-2" title={t('paymentMethods.usesCheckbookBadge', 'Utilise un chéquier')}>
          📄 {t('paymentMethods.checkbook', 'Chéquier')}
        </span>
      )}

      {/* System badge */}
      {paymentMethod.isSystem && (
        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 mr-3">
          Système
        </span>
      )}

      {/* Actions - only show if user can edit */}
      {canEdit && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {!paymentMethod.isSystem && (
            <>
              <button
                onClick={() => onEdit(paymentMethod)}
                className="p-1.5 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/50 text-slate-400 dark:text-slate-500 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                title="Modifier"
              >
                <PencilIcon />
              </button>
            </>
          )}
          <button
            onClick={() => onDelete(paymentMethod)}
            className="p-1.5 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/50 text-slate-400 dark:text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
            title="Supprimer"
          >
            <TrashIcon />
          </button>
        </div>
      )}
    </div>
  );
};

interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreatePaymentMethodRequest) => Promise<void>;
  editPaymentMethod?: PaymentMethod | null;
}

const PaymentMethodModal = ({ isOpen, onClose, onSave, editPaymentMethod }: PaymentMethodModalProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [usesCheckbook, setUsesCheckbook] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (editPaymentMethod) {
      setName(editPaymentMethod.name);
      setLogoUrl(editPaymentMethod.logoUrl || '');
      setUsesCheckbook(editPaymentMethod.usesCheckbook ?? false);
    } else {
      setName('');
      setLogoUrl('');
      setUsesCheckbook(false);
    }
  }, [editPaymentMethod, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      await onSave({
        name: name.trim(),
        logoUrl: logoUrl.trim() || undefined,
        usesCheckbook,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {editPaymentMethod 
            ? t('paymentMethods.modal.editTitle', 'Modifier le mode de paiement') 
            : t('paymentMethods.modal.createTitle', 'Nouveau mode de paiement')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('paymentMethods.modal.name', 'Nom')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              placeholder={t('paymentMethods.modal.namePlaceholder', 'Ex: Carte bancaire, PayPal...')}
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('paymentMethods.modal.icon', 'Icône')}
            </label>
            <div className="relative">
              <button
                ref={emojiButtonRef}
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-left flex items-center gap-2"
              >
                {logoUrl ? (
                  <>
                    <span className="text-2xl">{logoUrl}</span>
                    <span className="text-slate-500 dark:text-slate-400">{t('paymentMethods.modal.changeIcon', "Changer l'icône")}</span>
                  </>
                ) : (
                  <span className="text-slate-400 dark:text-slate-500">{t('paymentMethods.modal.selectIcon', 'Choisir une icône...')}</span>
                )}
              </button>
              
              {showEmojiPicker && (
                <div className="absolute top-full left-0 mt-2 z-50">
                  <EmojiPicker
                    value={logoUrl}
                    onChange={(emoji) => setLogoUrl(emoji)}
                    onClose={() => setShowEmojiPicker(false)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Uses Checkbook */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="usesCheckbook"
              checked={usesCheckbook}
              onChange={(e) => setUsesCheckbook(e.target.checked)}
              className="w-4 h-4 text-violet-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-violet-500"
            />
            <label htmlFor="usesCheckbook" className="text-sm text-slate-700 dark:text-slate-300">
              {t('paymentMethods.modal.usesCheckbook', 'Utilise un chéquier')}
            </label>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2 ml-7">
            {t('paymentMethods.modal.usesCheckbookHelp', 'Cochez cette case pour activer la sélection et numérotation automatique des chèques lors de la saisie d\'opérations')}
          </p>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DeleteModalProps {
  isOpen: boolean;
  paymentMethod: PaymentMethod | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteModal = ({ isOpen, paymentMethod, onClose, onConfirm }: DeleteModalProps) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen || !paymentMethod) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('paymentMethods.deleteModal.title', 'Supprimer le mode de paiement')}
        </h2>
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          {t('paymentMethods.deleteModal.message', 'Êtes-vous sûr de vouloir supprimer le mode de paiement "{{name}}" ? Cette action est irréversible.', { name: paymentMethod.name })}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 transition-colors"
          >
            {isDeleting ? t('common.loading') : t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

interface ResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const ResetModal = ({ isOpen, onClose, onConfirm }: ResetModalProps) => {
  const { t } = useTranslation();
  const [isResetting, setIsResetting] = useState(false);

  const handleReset = async () => {
    setIsResetting(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsResetting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('paymentMethods.removeTemplateModal.title', 'Retirer le template')}
        </h2>
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          {t('paymentMethods.removeTemplateModal.message', 'Cette action supprimera tous les modes de paiement issus du template. Vos modes de paiement personnalisés seront conservés.')}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleReset}
            disabled={isResetting}
            className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 transition-colors"
          >
            {isResetting ? t('common.loading') : t('paymentMethods.removeTemplateModal.confirm', 'Retirer le template')}
          </button>
        </div>
      </div>
    </div>
  );
};

interface TemplatesPanelProps {
  onTemplateAdded: () => void;
  canEdit: boolean;
}

const TemplatesPanel = ({ onTemplateAdded, canEdit }: TemplatesPanelProps) => {
  const { t } = useTranslation();
  const { templates, isLoading, addTemplate } = usePaymentMethodTemplatesStore();
  const [isExpanded, setIsExpanded] = useState(true);

  // Hide template panel in read-only mode or when no templates available
  if (!canEdit || templates.length === 0) {
    return null;
  }

  const handleAddTemplate = async (templateId: string) => {
    const result = await addTemplate(templateId);
    if (result) {
      onTemplateAdded();
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-t-xl transition-colors"
      >
        <h2 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
          <CreditCardIcon />
          {t('paymentMethods.templates.title', 'Templates disponibles')}
          <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
            ({templates.length})
          </span>
        </h2>
        {isExpanded ? (
          <ChevronDownIcon className="w-5 h-5 text-slate-400 dark:text-slate-500" />
        ) : (
          <ChevronRightIcon className="w-5 h-5 text-slate-400 dark:text-slate-500" />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-4">
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
            {t('paymentMethods.templates.description', 'Ajoutez des modes de paiement par défaut que vous avez supprimés.')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {templates.map((template) => (
              <div
                key={template.id}
                className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{template.logoUrl}</span>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{template.name}</span>
                </div>
                <button
                  onClick={() => handleAddTemplate(template.id)}
                  disabled={isLoading}
                  className="px-2 py-1 text-xs bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300 rounded hover:bg-violet-200 dark:hover:bg-violet-900 disabled:opacity-50 transition-colors"
                >
                  {t('common.add', 'Ajouter')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function PaymentMethodsPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const { canManagePaymentMethods } = useAuthStore();
  const canEdit = canManagePaymentMethods();
  const { 
    paymentMethods, 
    isLoading, 
    error, 
    fetchPaymentMethods, 
    createPaymentMethod, 
    updatePaymentMethod, 
    deletePaymentMethod,
    resetTemplates,
    removeTemplate,
    purgeUnused,
    clearError 
  } = usePaymentMethodStore();

  const { 
    fetchAvailableTemplates 
  } = usePaymentMethodTemplatesStore();

  const [showModal, setShowModal] = useState(false);
  const [editPaymentMethod, setEditPaymentMethod] = useState<PaymentMethod | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PaymentMethod | null>(null);
  const [showRemoveTemplateModal, setShowRemoveTemplateModal] = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [resetResult, setResetResult] = useState<{ addedCount: number } | null>(null);
  const [purgeResult, setPurgeResult] = useState<number | null>(null);

  useEffect(() => {
    fetchPaymentMethods();
    fetchAvailableTemplates();
  }, [fetchPaymentMethods, fetchAvailableTemplates]);

  const handleAdd = () => {
    setEditPaymentMethod(null);
    setShowModal(true);
  };

  const handleEdit = (paymentMethod: PaymentMethod) => {
    setEditPaymentMethod(paymentMethod);
    setShowModal(true);
  };

  const handleDelete = (paymentMethod: PaymentMethod) => {
    setDeleteTarget(paymentMethod);
  };

  const handleSave = async (data: CreatePaymentMethodRequest) => {
    if (editPaymentMethod) {
      await updatePaymentMethod(editPaymentMethod.id, {
        name: data.name,
        logoUrl: data.logoUrl,
        usesCheckbook: data.usesCheckbook,
      });
    } else {
      await createPaymentMethod(data);
    }
    fetchAvailableTemplates();
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      await deletePaymentMethod(deleteTarget.id);
      fetchAvailableTemplates();
    }
  };

  const handleResetTemplates = async () => {
    const result = await resetTemplates();
    if (result) {
      setResetResult(result);
      fetchAvailableTemplates();
    }
  };

  const handleRemoveTemplate = async () => {
    const count = await removeTemplate();
    if (count > 0) {
      setResetResult({ addedCount: -count }); // negative to indicate removal
      fetchAvailableTemplates();
    }
  };

  const handlePurgeUnused = async () => {
    setIsPurging(true);
    try {
      const count = await purgeUnused();
      setPurgeResult(count);
      setTimeout(() => setPurgeResult(null), 3000);
    } finally {
      setIsPurging(false);
      setShowPurgeModal(false);
    }
  };

  const handleTemplateAdded = () => {
    fetchPaymentMethods();
    fetchAvailableTemplates();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className={`${embedded ? 'text-xl' : 'text-2xl'} font-bold text-slate-900 dark:text-slate-100`}>{t('paymentMethods.title', 'Modes de paiement')}</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t('paymentMethods.subtitle', 'Gérez vos modes de paiement')}</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleResetTemplates}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-2 text-sm text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t('paymentMethods.loadTemplate', 'Charger le template')}
            >
              <DownloadIcon />
              <span className="hidden md:inline">{t('paymentMethods.loadTemplate', 'Charger le template')}</span>
            </button>
            <button
              onClick={() => setShowRemoveTemplateModal(true)}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-2 text-sm text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/30 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t('paymentMethods.removeTemplate', 'Retirer le template')}
            >
              <RemoveIcon />
              <span className="hidden md:inline">{t('paymentMethods.removeTemplate', 'Retirer le template')}</span>
            </button>
            <button
              onClick={() => setShowPurgeModal(true)}
              disabled={isLoading || isPurging}
              className="flex items-center gap-2 px-3 py-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t('paymentMethods.purgeUnused', 'Épurer les non utilisés')}
            >
              <PurgeIcon />
              <span className="hidden md:inline">{t('paymentMethods.purgeUnused', 'Épurer')}</span>
            </button>
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
            >
              <PlusIcon />
              <span className="hidden md:inline">{t('paymentMethods.add', 'Ajouter')}</span>
            </button>
          </div>
        )}
      </div>

      {/* Success message for purge */}
      {purgeResult !== null && (
        <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 flex items-center justify-between">
          <p className="text-emerald-700 dark:text-emerald-300">
            {purgeResult > 0 
              ? t('paymentMethods.purgeSuccess', 'Modes de paiement personnels non utilisés supprimés : {{count}}', { count: purgeResult })
              : t('paymentMethods.purgeNone', 'Aucun mode de paiement personnel non utilisé à supprimer')
            }
          </p>
          <button
            onClick={() => setPurgeResult(null)}
            className="text-emerald-500 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
          >
            ✕
          </button>
        </div>
      )}

      {/* Success message for reset */}
      {resetResult && (
        <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 flex items-center justify-between">
          <p className="text-emerald-700 dark:text-emerald-300">
            {resetResult.addedCount >= 0 
              ? t('paymentMethods.loadTemplateSuccess', 'Modes de paiement du template chargés : {{count}}', { count: resetResult.addedCount })
              : t('paymentMethods.removeTemplateSuccess', 'Modes de paiement du template retirés : {{count}}', { count: Math.abs(resetResult.addedCount) })
            }
          </p>
          <button
            onClick={() => setResetResult(null)}
            className="text-emerald-500 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
          >
            ✕
          </button>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-lg p-4 flex items-center justify-between">
          <p className="text-rose-700 dark:text-rose-300">{error}</p>
          <button
            onClick={clearError}
            className="text-rose-500 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300"
          >
            ✕
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading && paymentMethods.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-violet-200 dark:border-violet-800 border-t-violet-600 dark:border-t-violet-400 rounded-full animate-spin" />
        </div>
      )}

      {/* Payment methods list */}
      {!isLoading && paymentMethods.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
            <CreditCardIcon />
          </div>
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t('paymentMethods.empty.title', 'Aucun mode de paiement')}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            {canEdit 
              ? t('paymentMethods.empty.message', 'Créez votre premier mode de paiement pour commencer.')
              : t('paymentMethods.empty.messageReadOnly', 'Aucun mode de paiement n\'a encore été créé.')
            }
          </p>
          {canEdit && (
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
            >
              <PlusIcon />
              <span>{t('paymentMethods.empty.create', 'Créer un mode de paiement')}</span>
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-violet-50 dark:bg-violet-900/30 rounded-t-xl">
            <h2 className="font-semibold text-violet-700 dark:text-violet-300 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-violet-500" />
              {t('paymentMethods.listTitle', 'Vos modes de paiement')}
              <span className="text-sm font-normal text-violet-500 dark:text-violet-400">
                ({paymentMethods.length})
              </span>
            </h2>
          </div>
          <div className="py-2">
            {paymentMethods.map((paymentMethod) => (
              <PaymentMethodNode
                key={paymentMethod.id}
                paymentMethod={paymentMethod}
                onEdit={handleEdit}
                onDelete={handleDelete}
                canEdit={canEdit}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available Templates Panel */}
      <TemplatesPanel onTemplateAdded={handleTemplateAdded} canEdit={canEdit} />

      {/* Modals */}
      <PaymentMethodModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditPaymentMethod(null);
        }}
        onSave={handleSave}
        editPaymentMethod={editPaymentMethod}
      />

      <DeleteModal
        isOpen={!!deleteTarget}
        paymentMethod={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />

      <ResetModal
        isOpen={showRemoveTemplateModal}
        onClose={() => setShowRemoveTemplateModal(false)}
        onConfirm={handleRemoveTemplate}
      />

      {/* Purge Modal */}
      {showPurgeModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowPurgeModal(false)} />
            <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
                {t('paymentMethods.purgeModal.title', 'Épurer les modes de paiement')}
              </h2>
              <p className="text-slate-600 dark:text-slate-300 mb-6">
                {t('paymentMethods.purgeModal.message', 'Cette action supprimera tous les modes de paiement personnels (non issus de templates) qui ne sont utilisés dans aucune opération ni échéancier.')}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowPurgeModal(false)}
                  className="px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                >
                  {t('common.cancel', 'Annuler')}
                </button>
                <button
                  onClick={handlePurgeUnused}
                  disabled={isPurging}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
                >
                  {isPurging ? t('common.loading', 'Chargement...') : t('paymentMethods.purgeModal.confirm', 'Épurer')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
